import { Button, FormField, Modal, Select } from '@chopchop-se/ui';
import { printerCreateSchema, type Printer, type PrinterCreate } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Create / edit a printer.
 *
 * The host field is validated with the SAME zod schema the API uses
 * (`printerCreateSchema`), so "is this a plausible printer address" has one
 * answer rather than a client-side one and a server-side one. It is also the
 * only place in the app where a host is ever typed — print requests carry a
 * printer id, never an address.
 */
export interface PrinterFormValues {
  name: string;
  host: string;
  port: string;
  model: string;
  kind: 'label' | 'document';
  station: string;
  stockCode: string;
  isDefault: boolean;
  isActive: boolean;
}

export function emptyPrinterForm(): PrinterFormValues {
  return {
    name: '',
    host: '',
    port: '9100',
    model: '',
    kind: 'label',
    station: '',
    stockCode: '',
    isDefault: false,
    isActive: true,
  };
}

export function printerToForm(printer: Printer): PrinterFormValues {
  return {
    name: printer.name,
    host: printer.host,
    port: String(printer.port),
    model: printer.model ?? '',
    kind: printer.kind,
    station: printer.station ?? '',
    stockCode: printer.stockCode ?? '',
    isDefault: printer.isDefault,
    isActive: printer.isActive,
  };
}

export interface ValidationResult {
  ok: boolean;
  values?: PrinterCreate;
  errors: Partial<Record<keyof PrinterFormValues, string>>;
}

/** Exported for the unit test — the form's whole contract in one function. */
export function validatePrinterForm(values: PrinterFormValues): ValidationResult {
  const parsed = printerCreateSchema.safeParse({
    name: values.name,
    host: values.host,
    port: Number(values.port),
    model: values.model || null,
    kind: values.kind,
    station: values.station || null,
    stockCode: values.stockCode || null,
    isDefault: values.isDefault,
    isActive: values.isActive,
  });

  if (parsed.success) return { ok: true, values: parsed.data, errors: {} };

  const errors: Partial<Record<keyof PrinterFormValues, string>> = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string') errors[key as keyof PrinterFormValues] = issue.message;
  }
  return { ok: false, errors };
}

export function PrinterFormModal({
  initial,
  saving,
  onSubmit,
  onClose,
}: {
  initial: PrinterFormValues;
  saving: boolean;
  onSubmit: (values: PrinterCreate) => void;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const [values, setValues] = useState<PrinterFormValues>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof PrinterFormValues, string>>>({});

  const set = <K extends keyof PrinterFormValues>(key: K, value: PrinterFormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  return (
    <Modal open onClose={onClose} title={t('devicesPrinting:printers.edit')} width={560}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          const result = validatePrinterForm(values);
          setErrors(result.errors);
          if (result.ok && result.values) onSubmit(result.values);
        }}
      >
        <FormField
          label={t('devicesPrinting:printers.form.name')}
          placeholder={t('devicesPrinting:printers.form.namePlaceholder')}
          value={values.name}
          error={errors.name}
          onChange={(event) => {
            set('name', event.target.value);
          }}
        />

        <div className="grid grid-cols-[1fr_auto] gap-3">
          <FormField
            label={t('devicesPrinting:printers.form.host')}
            placeholder={t('devicesPrinting:printers.form.hostPlaceholder')}
            value={values.host}
            error={errors.host}
            hint={<span className="text-xs">{t('devicesPrinting:printers.form.hostHelp')}</span>}
            onChange={(event) => {
              set('host', event.target.value);
            }}
          />
          <FormField
            label={t('devicesPrinting:printers.form.port')}
            className="w-28"
            inputMode="numeric"
            value={values.port}
            error={errors.port}
            onChange={(event) => {
              set('port', event.target.value);
            }}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('devicesPrinting:printers.form.kind')}
            value={values.kind}
            options={[
              { value: 'label', label: t('devicesPrinting:printers.kind.label') },
              { value: 'document', label: t('devicesPrinting:printers.kind.document') },
            ]}
            onChange={(event) => {
              set('kind', event.target.value === 'document' ? 'document' : 'label');
            }}
          />
          <FormField
            label={t('devicesPrinting:printers.form.model')}
            placeholder={t('devicesPrinting:printers.form.modelPlaceholder')}
            value={values.model}
            onChange={(event) => {
              set('model', event.target.value);
            }}
          />
          <FormField
            label={t('devicesPrinting:printers.form.station')}
            placeholder={t('devicesPrinting:printers.form.stationPlaceholder')}
            value={values.station}
            onChange={(event) => {
              set('station', event.target.value);
            }}
          />
          <FormField
            label={t('devicesPrinting:printers.form.stockCode')}
            value={values.stockCode}
            hint={<span className="text-xs">{t('devicesPrinting:printers.form.stockCodeHint')}</span>}
            onChange={(event) => {
              set('stockCode', event.target.value);
            }}
          />
        </div>

        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={values.isDefault}
              onChange={(event) => {
                set('isDefault', event.target.checked);
              }}
            />
            {t('devicesPrinting:printers.form.isDefault')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(event) => {
                set('isActive', event.target.checked);
              }}
            />
            {t('devicesPrinting:printers.form.isActive')}
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" loading={saving}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
